import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppService } from '@app/app.service';
import { ChangeCryptoProCertificateDialogComponent } from '@app/dialogs-helper/change-crypto-pro-certificate-dialog/change-crypto-pro-certificate-dialog.component';
import { DialogComponent } from '@components/dialog/component/dialog.component';
import { CadeSysPluginData } from '@components/fields/file/interfaces/file.component.interface';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import { Certificate } from 'crypto-pro';
import { SignService } from 'crypto-pro-ts';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class SignAppService {
    /** Модуль КриптоПро */
    private _cryptoProSignService: SignService = null;
    constructor(
        private readonly _notifier: NotifierService,
        private readonly _appService: AppService,
        private readonly _dialog: MatDialog,
    ) {}

    /** Инициализация модуля плагина */
    public async initModule() {
        return new Promise((resolve, reject) => {
            import('crypto-pro-ts/plugin/cadesplugin_api')
                .then(() => {
                    const plugin: any = window.cadesplugin;
                    plugin.catch((err) => {
                        reject(err);
                    });
                    import('crypto-pro-ts')
                        .then((cryptoModule) => {
                            try {
                                this._cryptoProSignService = new cryptoModule.SignService();
                                resolve(this._cryptoProSignService);
                            } catch (err) {
                                reject(err);
                            }
                        })
                        .catch((err) => {
                            reject(err);
                        });
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    /** Выбор сертификата пользователем */
    public selectCertificates(): Observable<Certificate> {
        let pluginData: CadeSysPluginData = null;
        return this.getSystemInfo().pipe(
            take(1),
            switchMap((plugData: CadeSysPluginData) => {
                pluginData = plugData;
                return this.getCertificateList(true);
            }),
            switchMap((certificates: Certificate[]) => {
                return this.selectCertificateEPDialog(certificates, pluginData);
            }),
        );
    }

    /**
     * Открыть диалог выбора сертификата
     * @param certificates
     * @param systemInfo
     * @private
     */
    private selectCertificateEPDialog(
        certificates: Certificate[],
        systemInfo: CadeSysPluginData,
    ): Observable<Certificate | null> {
        return DialogComponent.commonDialog(this._dialog, ChangeCryptoProCertificateDialogComponent, {
            width: '50vw',
            disableClose: true,
            autoFocus: false,
            data: {
                certificates,
                systemInfo,
            },
        })
            .afterClosed()
            .pipe(map((result) => result?.data));
    }

    /** Получить информацию о плагине и системе */
    public getSystemInfo(): Observable<CadeSysPluginData | null> {
        const data$: Subject<CadeSysPluginData> = new Subject();
        this._cryptoProSignService.getSystemInfo().subscribe({
            next: (data: CadeSysPluginData) => {
                if (!data.hasOwnProperty('cspVersion') || !data.hasOwnProperty('cadesVersion')) {
                    this._notifier.notify(
                        ENotifyTypes.error,
                        this._appService.language_dictionary?.system_subscribe_plug_in_setup_error,
                    );
                    return;
                }
                data$.next(data);
            },
            error: (error) => {
                data$.error(error);
            },
        });
        return data$.asObservable();
    }

    /**
     * Получение списка сертификатов пользователя на устройстве
     * @param resetCache - флаг для сброса кеша
     */
    public getCertificateList(resetCache: boolean = true): Observable<Certificate[]> {
        const data$: Subject<Certificate[] | any> = new Subject();
        this._cryptoProSignService.getCertsList(resetCache).subscribe({
            next: (data) => {
                if (data?.length) {
                    data$.next(data);
                } else {
                    data$.complete();
                    this._notifier.notify(
                        ENotifyTypes.warning,
                        this._appService.language_dictionary.system_subscribe_plug_in_empty_error,
                    );
                }
            },
            error: (error) => {
                data$.error(error);
            },
        });
        return data$.asObservable();
    }

    /**
     * Подписать файл
     * @param certificate - выбранный сертификат
     * @param file - данные файла
     */
    public signFile(certificate: Certificate, file: File): Observable<string> {
        const data$: Subject<string> = new Subject();
        this._cryptoProSignService.createFileSign(file, certificate.thumbprint).subscribe({
            next: (sign) => {
                data$.next(sign);
            },
            error: (error) => {
                data$.error(error);
                data$.complete();
            },
        });
        return data$;
    }
}
